/**
 * House of Dusk · fiche huile (lot 6) : selecteur de parfum et liens qui suivent le parfum.
 * Contrat de la coque : 00_AI_BRAIN/06_RELEVE_THEME/lots/lot5-6_coque.md (<hod-product>).
 *
 * <hod-scent-select> (snippet hod-scent-select) :
 *   - declencheur : ouvre et ferme le panneau (aria-expanded, « Change » / « Close ») ; a
 *     l'ouverture, la puce choisie est ramenee dans le panneau et le panneau au-dessus de la
 *     barre fixe (scroll-margin de hod-pdp-oil.css) ;
 *   - puces radio (fiche a vingt variantes) : un choix appelle root.selectVariant(id) de
 *     <hod-product> (aucun fetch : H1, prix, specs, texte, bouton, barre fixe, ?variant=) puis
 *     referme le panneau et rend le focus au declencheur. Au clavier, les fleches parcourent
 *     les parfums sans refermer (le choix suit), Entree ou Espace referment, Echap referme ;
 *   - puces liens (vingt fiches liees, D16) : navigation normale, Echap referme ;
 *   - suit hod:pdp:variant (pastille, nom, puce choisie, etat epuise).
 *   Apercu de review : ?hod_preview=scents ouvre le panneau au chargement ; dans l'editeur de
 *   theme, selectionner le bloc ouvre le panneau.
 * [data-hod-href-map] (renvoi vers le set, bouton du scent guide) : table variante vers adresse,
 *   appliquee a chaque hod:pdp:variant.
 */

const parse = (raw, fallback = null) => {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};
const ARROWS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']);
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class HodScentSelect extends HTMLElement {
  // Initialisation unique ; ecoutes reposees a chaque branchement (retirees au debranchement).
  connectedCallback() {
    const first = !this.bound;
    if (first) {
      this.bound = true;
      this.trigger = this.querySelector('[data-hod-scent-trigger]');
      this.panel = this.querySelector('[data-hod-scent-panel]');
      this.chips = this.querySelector('[data-hod-scent-chips]');
      this.stateLabel = this.querySelector('[data-hod-scent-state]');
      this.nameEl = this.querySelector('[data-hod-scent-current]');
      this.dotEl = this.querySelector('[data-hod-scent-dot]');
      this.arrowNav = false;
    }
    this.root = this.closest('hod-product');
    if (!this.trigger || !this.panel) return;

    this.trigger.addEventListener('click', this.onTrigger);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('change', this.onChange);
    this.panel.addEventListener('click', this.onPanelClick);
    if (this.root) this.root.addEventListener('hod:pdp:variant', this.onVariant);
    document.addEventListener('shopify:block:select', this.onEditor);
    document.addEventListener('shopify:block:deselect', this.onEditor);

    const preview = first ? new URLSearchParams(window.location.search).get('hod_preview') : '';
    if (preview === 'scents') this.open({ scroll: false });
  }

  disconnectedCallback() {
    if (!this.trigger || !this.panel) return;
    this.trigger.removeEventListener('click', this.onTrigger);
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('change', this.onChange);
    this.panel.removeEventListener('click', this.onPanelClick);
    if (this.root) this.root.removeEventListener('hod:pdp:variant', this.onVariant);
    document.removeEventListener('shopify:block:select', this.onEditor);
    document.removeEventListener('shopify:block:deselect', this.onEditor);
  }

  // Editeur de theme : le panneau s'ouvre quand le bloc est selectionne, se referme ensuite.
  onEditor = (event) => {
    const target = event.target instanceof Node ? event.target : null;
    if (!target || !(target === this || this.contains(target))) return;
    if (event.type === 'shopify:block:select') this.open({ scroll: false });
    else this.close();
  };

  get isOpen() {
    return this.trigger.getAttribute('aria-expanded') === 'true';
  }

  get closeOnPick() {
    return this.dataset.closeOnPick !== 'false';
  }

  onTrigger = () => {
    if (this.isOpen) this.close();
    else this.open();
  };

  open({ scroll = true } = {}) {
    this.panel.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.setStateLabel(true);
    this.revealCurrent();
    if (scroll) {
      // Le panneau passe au-dessus de la barre fixe s'il tombait dessous (iPhone SE, 05 §3d).
      requestAnimationFrame(() => {
        this.panel.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduceMotion() ? 'instant' : 'smooth' });
      });
    }
    this.dispatchEvent(new CustomEvent('hod:scent:open', { bubbles: true }));
  }

  close({ focus = false } = {}) {
    if (!this.isOpen) return;
    const inside = this.panel.contains(document.activeElement);
    this.panel.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.setStateLabel(false);
    if (focus || inside) this.trigger.focus({ preventScroll: true });
    this.dispatchEvent(new CustomEvent('hod:scent:close', { bubbles: true }));
  }

  setStateLabel(open) {
    const el = this.stateLabel;
    if (!el) return;
    const text = open ? el.dataset.labelOpen : el.dataset.labelClosed;
    if (text) el.textContent = text;
  }

  // La puce choisie visible dans le panneau (defilement horizontal seulement).
  revealCurrent() {
    const box = this.chips;
    const chip = box && box.querySelector('.is-on');
    if (!box || !chip || box.scrollWidth <= box.clientWidth + 1) return;
    const left = chip.offsetLeft;
    const right = left + chip.offsetWidth;
    if (left >= box.scrollLeft && right <= box.scrollLeft + box.clientWidth) return;
    box.scrollLeft = Math.max(0, left - (box.clientWidth - chip.offsetWidth) / 2);
  }

  onKeydown = (event) => {
    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.close({ focus: true });
      return;
    }
    const input = event.target instanceof HTMLInputElement && event.target.type === 'radio' ? event.target : null;
    if (!input) return;
    if (ARROWS.has(event.key)) {
      this.arrowNav = true;
      setTimeout(() => {
        this.arrowNav = false;
      }, 0);
    } else if (event.key === 'Enter' || event.key === ' ') {
      // Entree ou Espace sur une puce : valide et referme (Espace ne declenche pas toujours de
      // clic sur une radio deja cochee).
      event.preventDefault();
      if (!input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.close({ focus: true });
    }
  };

  // Clic sur une puce (y compris la puce deja choisie, comme la maquette) : referme.
  // Les fleches declenchent aussi un clic sur la radio suivante : ignore dans ce cas.
  onPanelClick = (event) => {
    const input = event.target instanceof HTMLInputElement && event.target.type === 'radio' ? event.target : null;
    if (!input || this.arrowNav || !this.closeOnPick) return;
    requestAnimationFrame(() => this.close({ focus: true }));
  };

  onChange = (event) => {
    const input = event.target instanceof HTMLInputElement && event.target.type === 'radio' ? event.target : null;
    // <hod-product> ignore ce change : la radio ne porte pas data-hod-option-input.
    if (!input || !this.contains(input) || !input.checked) return;
    this.markChecked(input);
    this.showCurrent(input);
    const root = this.root;
    if (root && typeof root.selectVariant === 'function') {
      root.selectVariant(input.value, { source: 'scent' });
    } else if (root) {
      customElements.whenDefined('hod-product').then(() => root.selectVariant(input.value, { source: 'scent' }));
    }
  };

  // Variante changee ailleurs (API, autre selecteur) : puce, pastille et nom suivent.
  onVariant = (event) => {
    const variant = event.detail && event.detail.variant;
    if (!variant) return;
    const input = this.querySelector(`input[type="radio"][value="${String(variant.id)}"]`);
    if (!input) return;
    if (!input.checked) input.checked = true;
    this.markChecked(input);
    this.showCurrent(input);
    const chip = input.closest('.hod-pdp-scent__chip');
    if (chip) chip.classList.toggle('is-unavailable', variant.available === false);
  };

  markChecked(input) {
    this.querySelectorAll('.hod-pdp-scent__chip').forEach((chip) => {
      chip.classList.toggle('is-on', chip.contains(input));
    });
  }

  showCurrent(input) {
    if (this.nameEl && input.dataset.name) this.nameEl.textContent = input.dataset.name;
    if (this.dotEl) {
      const dot = input.dataset.dot || '';
      this.dotEl.style.background = dot;
      this.dotEl.hidden = !dot;
    }
  }
}

if (!customElements.get('hod-scent-select')) customElements.define('hod-scent-select', HodScentSelect);

// Adresses qui suivent le parfum choisi (renvoi vers le set pre-rempli, scent guide ancre).
if (!window.__hodScentHrefs) {
  window.__hodScentHrefs = true;
  const maps = new WeakMap();
  document.addEventListener('hod:pdp:variant', (event) => {
    const detail = event.detail || {};
    const root = detail.root || event.target;
    const id = String(detail.variant ? detail.variant.id : '');
    if (!root || !id || typeof root.querySelectorAll !== 'function') return;
    root.querySelectorAll('[data-hod-href-map]').forEach((link) => {
      let map = maps.get(link);
      if (!map) {
        map = parse(link.getAttribute('data-hod-href-map'), {}) || {};
        maps.set(link, map);
      }
      if (Object.prototype.hasOwnProperty.call(map, id) && map[id]) link.setAttribute('href', map[id]);
    });
  });
}
